/**
 * A door that swings, deterministically, as often as anybody wants it to.
 *
 * No physics: pressing F stamps the simulation time and where the leaf had got
 * to at that moment, and everything after that is a pure function of
 * `simulationTime - swungAt`.
 *
 * ------------------------------------------------------------------------
 *  THE LEAF IS THE COLLIDER, AND IT ARRIVES WHERE YOU ARE STANDING
 * ------------------------------------------------------------------------
 *  It used to be a slab in the doorway that switched off a fifth of the way
 *  through the swing, so that a runner could press F and keep going. That made
 *  a door a hole that opened on a clock, and it meant the metre and a half of
 *  timber you could watch sweeping into the room went through people without
 *  touching them.
 *
 *  Now the boxes follow the leaf. `doorLeafBoxes` re-cuts them every step the
 *  angle moves, `CollisionWorld` pushes whoever is inside one out of it, and
 *  the quarter circle in front of every inward-opening door in the building is
 *  ground you cannot be standing on when it opens. What that buys is a door
 *  worth *positioning* for: open the pirtti's bedroom door from square in
 *  front of it and it shoves you back across the room, open it from a stride
 *  to the side and it goes past you into the space you left. The ulko-ovi at
 *  the far end of the käytävä opens the other way, into the yard, and is
 *  therefore the one you can take flat out. See `DOOR` in
 *  src/config/gameplay.ts.
 *
 *  Shutting one on yourself is still allowed and still costs exactly what it
 *  looks like it costs - the leaf comes back through the doorway and takes you
 *  with it, or ejects you out of the shallowest face if you were inside it
 *  when it landed. Deterministic, and nobody gets stuck; see
 *  `resolveHorizontal`.
 *
 * ------------------------------------------------------------------------
 *  A PRESS IS A REVERSE, NOT A SWITCH
 * ------------------------------------------------------------------------
 *  `from` is wherever the leaf actually was when F landed and `to` is the end
 *  it is now headed for, so a door caught halfway takes the half it has left
 *  rather than starting its swing again. That is the whole of what makes this
 *  a door you can stand and work rather than a two-position switch: press,
 *  press again, and the leaf turns round where it is.
 *
 *  Which is also why `canInteract` never latches. It used to: a door was a
 *  thing that could only happen once, so it stopped answering the moment it
 *  had. It answers for the whole run now, the way the sauna's locked one
 *  always has - see `Sauna` - and the prompt names the half the press would
 *  do rather than the door.
 *
 * No three.js in here. The swinging leaf is a mesh owned by World.ts, which
 * reads `angle` back out - that split is what lets a run be re-simulated
 * server-side in plain node, where there is no scene graph at all, and it is
 * what stops the picture and the collider disagreeing about where the door is.
 */

import { DOOR, INTERACT } from '../config/gameplay';
import { clamp, smoothStep, type Vec3 } from '../utils/math';
import type { Collider } from './CollisionWorld';
import { boxCenter, doorLeafBoxes, type DoorSpec } from './Level';
import { isLookingAt, type InteractContext, type Interactable } from './Interactable';

/** Which way the leaf was sent on the step F landed. */
export type DoorEvent = 'opened' | 'closed';

export class Door implements Interactable {
  readonly id: string;

  /** Where the leaf was when F was last pressed, where it is headed, and when. */
  private from = 0;
  private to = 0;
  private swungAt = 0;
  private progress = 0;
  /** The progress the boxes below were last cut at, so a still door is free. */
  private placedAt = Number.NaN;
  private readonly center: Vec3;

  constructor(
    readonly spec: DoorSpec,
    /** The leaf, in pieces. One per `DOOR.segments`; see `doorLeafBoxes`. */
    private readonly colliders: readonly Collider[],
  ) {
    this.id = spec.id;
    this.center = boxCenter(spec.box);
    this.place();
  }

  /**
   * What the HUD prints after `[F]`, which is the half of the door the press
   * would actually do. A leaf caught mid-swing is named by where it is *going*
   * rather than by where it has got to: the prompt answers "what happens if I
   * press this", and what happens is the reverse of the swing that is running.
   */
  get prompt(): string {
    return this.to > 0 ? this.spec.closeLabel : this.spec.label;
  }

  get openProgress(): number {
    return this.progress;
  }

  /**
   * Where the leaf is pointed, in radians about the hinge.
   *
   * The one answer, read by the collider and by the mesh alike. The ease is on
   * this side of the split rather than in `World` because a door that looked
   * like it was easing and shoved like it was not would be a door nobody could
   * learn - the swing is slow at both ends and quick through the middle, and
   * that is true of the thing that hits you.
   */
  get angle(): number {
    return this.spec.swing * DOOR.swingAngle * smoothStep(this.progress);
  }

  /** Headed open, which includes the swing that is still on its way there. */
  get isOpen(): boolean {
    return this.to > 0;
  }

  focusPoint(): Vec3 {
    return this.center;
  }

  /**
   * A doorknob's reach and a doorknob's cone, and no latch on it. What F aims
   * at is the doorway rather than the leaf, which is what keeps the reach
   * honest at both ends of the swing: a runner opening one is a stride short
   * of it, and a runner closing one behind him is stood in the opening looking
   * back through it. Both are where the hand goes anyway.
   */
  canInteract(context: InteractContext): boolean {
    return isLookingAt(context, this.center, INTERACT.maxDistance, INTERACT.minLookDot);
  }

  interact(context: InteractContext): void {
    this.from = this.progress;
    this.to = this.to > 0 ? 0 : 1;
    this.swungAt = context.simulationTime;
  }

  reset(): void {
    this.from = 0;
    this.to = 0;
    this.swungAt = 0;
    this.progress = 0;
    this.place();
  }

  /** Called from the fixed step: owns anything the simulation can observe. */
  simulate(simulationTime: number): void {
    const travelled = (simulationTime - this.swungAt) / DOOR.openDuration;
    this.progress =
      this.to > 0
        ? clamp(this.from + travelled, 0, 1)
        : clamp(this.from - travelled, 0, 1);
    this.place();
  }

  /**
   * Put the boxes where the leaf is. Skipped while the door is standing still,
   * which is almost every step of almost every run - and skipped on the
   * progress rather than on a flag, so a door that is asked twice for the same
   * step gets the same boxes either way.
   */
  private place(): void {
    if (this.progress === this.placedAt) return;
    this.placedAt = this.progress;
    const boxes = doorLeafBoxes(this.spec, this.angle, this.colliders.length);
    for (let i = 0; i < this.colliders.length; i++) this.colliders[i].box = boxes[i];
  }
}
